'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { BookOpen } from 'lucide-react';
import { courses, levelBadgeClass, type Course } from '../../data/courses';

function CourseCard({ course }: { course: Course }) {
  const ctaHref = course.href ?? '#academy-cta';
  const ctaLabel = course.free ? 'Get Free Guide' : 'Enroll Now';
  const CtaWrapper = course.href
    ? ({ children }: { children: React.ReactNode }) => (
        <Link href={course.href!} className="inline-flex w-full">
          {children}
        </Link>
      )
    : ({ children }: { children: React.ReactNode }) => (
        <a href={ctaHref} className="inline-flex w-full">
          {children}
        </a>
      );

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md transition-all duration-300 hover:-translate-y-2 hover:border-pink-400/30 hover:shadow-lg hover:shadow-pink-500/10">
      <div className="relative aspect-video w-full overflow-hidden">
        <Image
          src={course.image}
          alt={course.imageAlt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 560px"
          className="object-cover object-center transition-transform duration-500 group-hover:scale-110"
        />
        <div
          className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold tracking-wide sm:left-5 sm:top-5 sm:px-4 sm:py-1.5 sm:text-sm ${
            course.badge ? 'bg-green-500/90 text-green-950' : levelBadgeClass[course.level]
          }`}
        >
          {course.badge ?? course.level}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-7 lg:p-10">
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-pink-100/70 sm:mb-5 sm:gap-5 sm:text-base lg:text-lg">
          <span className="inline-flex items-center gap-2 font-medium">
            <BookOpen className="h-4 w-4 text-pink-400 sm:h-5 sm:w-5" aria-hidden />
            {course.lessons} {course.lessonLabel}
          </span>
          <span className="inline-flex items-center gap-2 font-medium text-pink-200/60">
            {course.free ? '10-page PDF' : 'Video course'}
          </span>
        </div>

        <h3 className="mb-3 text-xl font-bold leading-snug text-white transition-colors group-hover:text-pink-300 sm:mb-4 sm:text-2xl lg:text-3xl">
          {course.title}
        </h3>
        <p className="mb-8 flex-1 text-base leading-relaxed text-pink-100/65 sm:mb-10 sm:text-lg">
          {course.description}
        </p>

        <CtaWrapper>
          <span className="inline-flex w-full min-h-12 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-500 to-purple-700 py-4 text-base font-semibold text-white shadow-md transition-transform duration-200 group-hover:scale-[1.03] hover:shadow-lg hover:shadow-pink-500/30 sm:py-5 sm:text-lg lg:text-xl">
            {ctaLabel}
          </span>
        </CtaWrapper>
      </div>
    </article>
  );
}

export default function CoursesGridClient() {
  return (
    <div>
      <div className="mb-8 text-center md:mb-16">
        <h2 className="mb-3 text-2xl font-bold text-white sm:mb-4 sm:text-3xl md:text-4xl">Explore Our Programs</h2>
        <p className="mx-auto max-w-2xl px-1 text-sm leading-relaxed text-pink-100/65 sm:text-base md:text-lg">
          Video courses and free playbooks on building deeper AI connections and regaining balance when the app
          starts running you.
        </p>
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-2 lg:gap-10 xl:max-w-7xl xl:gap-12">
        {courses.map((course) => (
          <CourseCard key={course.id} course={course} />
        ))}
      </div>
    </div>
  );
}
